import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  invoiceProps,
  subscriptionProps,
  subscriptionData,
  deviceDeactivateProps,
  ILicenses,
  IUpdateRenewalSubscriptions,
  IActiveTrail,
} from "../../types/details-api-types";

const BASE_URL = `${process.env.REACT_APP_API_URL}api/details`;

export const detailsApi = createApi({
  reducerPath: "detailsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { endpoint }) => {
      const token = localStorage.getItem("accessToken") ?? sessionStorage.getItem("accessToken");
      if (token && endpoint !== "login") {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),

  endpoints: (builder) => ({
    getInvoices: builder.query<invoiceProps[], null>({
      query: () => ({
        url: "/invoices",
        method: "POST",
        body: {
          api_token: localStorage.getItem("accessToken") ?? sessionStorage.getItem("accessToken"),
        },
      }),
    }),

    getDevices: builder.query<subscriptionProps[], null>({
      query: () => {
        return {
          url: "/devices",
          method: "POST",
          body: {
            api_token: localStorage.getItem("accessToken") ?? sessionStorage.getItem("accessToken"),
          },
        };
      },
    }),

    getSubscriptions: builder.query<subscriptionData[], null>({
      query: () => ({
        url: "/subscriptions",
        method: "POST",
        body: {
          api_token: localStorage.getItem("accessToken") ?? sessionStorage.getItem("accessToken"),
        },
      }),
    }),

    getLicenses: builder.query<ILicenses[], null>({
      query: () => ({
        url: "/licenses",
        method: "POST",
        body: {
          api_token: localStorage.getItem("accessToken") ?? sessionStorage.getItem("accessToken"),
        },
      }),
    }),

    setDeactivateDevice: builder.mutation<null, deviceDeactivateProps>({
      query: ({ machine_id, os, license_id }) => {
        return {
          url: "/device/deactivate",
          method: "POST",
          body: {
            api_token: localStorage.getItem("accessToken") ?? sessionStorage.getItem("accessToken"),
            machine_id: machine_id,
            os: os,
            license_id: license_id,
          },
        };
      },
    }),

    setRenewalSubscription: builder.mutation<null, IUpdateRenewalSubscriptions>({
      query: ({subscription_id}) => {
        return {
          url: "/update-renewal-subscriptions",
          method: "POST",
          body: {
            api_token: localStorage.getItem("accessToken") ?? sessionStorage.getItem("accessToken"),
            subscription_id: subscription_id,
          },
        };
      }
    }),

    activeTrialEvent: builder.mutation<null, IActiveTrail>({
      query: ({email, activeTrailData}) => {
        return {
          url: "/trigger-active-trail-event",
          method: "POST",
          body: {
            api_token: localStorage.getItem("accessToken") ?? sessionStorage.getItem("accessToken"),
            email: email,
            activeTrailData: activeTrailData,
            },
        };
      }
    }),

    survey: builder.mutation<null, { email: string, user_id: string, reason: string, survey: number, reasonId: string}>({
      query: ({email, user_id, reason, survey, reasonId}) => {
        return {
          url: "/update-survey",
          method: "POST",
          body: {
            api_token: localStorage.getItem("accessToken") ?? sessionStorage.getItem("accessToken"),
            email: email,
            user_id: user_id,
            reason: reason,
            survey: survey,
            reasonId: reasonId,
            },
        };
      }
    }),
  }),
});

export const {
  useGetInvoicesQuery,
  useGetDevicesQuery,
  useGetSubscriptionsQuery,
  useGetLicensesQuery,
  useSetDeactivateDeviceMutation,
  useSetRenewalSubscriptionMutation,
  useActiveTrialEventMutation,
  useSurveyMutation
} = detailsApi;
